/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { API_URLS } from '../../constants/ApiUrls';
import { eosGet, eosPost } from '../../services/EosApi';
import DATEFORMAT from '../../utils/date-format';
import { IDashboardEOSState } from '../types/dashboard.types';
import {
  FetchWidgetDataParams,
  IScenarioAnalysisState,
  IWidget,
  WidgetDataState,
} from '../types/scenarioAnalysis.types';
import {
  mergeWidgetsWithLayouts,
  normalizeWidgets,
  processWidgetData,
} from '../utils/scenarioAnalysisUtils';

type ScenarioAnalysisStateShape = {
  dashboardEOS: IDashboardEOSState;
  scenarioAnalysis: IScenarioAnalysisState;
};

const createErrorMessage = (error: unknown, defaultMessage: string): string =>
  error instanceof Error ? error.message : defaultMessage;

const createDefaultWidgetDataState = (): WidgetDataState => ({
  loading: false,
  error: null,
  rawData: [],
  chartData: [],
  seriesKeys: [],
  seriesUoms: {},
});

const updateWidgetProperty = (
  widgets: IWidget[],
  widgetId: string,
  updates: Partial<IWidget>,
): IWidget[] =>
  widgets.map((widget) => (widget.id === widgetId ? { ...widget, ...updates } : widget));

const initialState: IScenarioAnalysisState = {
  lastRefreshTime: null,
  savedWidgets: [],
  widgets: {
    loading: false,
    saving: false,
    data: [],
    error: null,
    maxZIndex: 1,
    currentCountryId: null,
    currentContractId: null,
  },
  widgetLayouts: {},
  widgetData: {},
};

export const fetchWidgetsBasedOnFilters = createAsyncThunk(
  'scenarioAnalysis/fetchWidgetsBasedOnFilters',
  async (
    { userId, supplyContractId }: { userId: number; supplyContractId: number | null },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as ScenarioAnalysisStateShape;
      const { selectedPortfolioId } = state.dashboardEOS.portfolio;
      const { selectedCountryId } = state.dashboardEOS.scenarioFilters;
      const response = await eosGet(
        `${API_URLS.SCENARIO_ANALYSIS_PREFERENCES_GET}?userId=${userId}&supplyContractId=${supplyContractId}&portfolioId=${selectedPortfolioId}&marketId=${selectedCountryId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(createErrorMessage(error, 'Unknown error occurred'));
    }
  },
);

export const fetchWidgetData = createAsyncThunk(
  'scenarioAnalysis/fetchWidgetData',
  async (params: FetchWidgetDataParams, { rejectWithValue, getState }) => {
    try {
      const state = getState() as ScenarioAnalysisStateShape;
      const { selectedPortfolioId } = state.dashboardEOS.portfolio;
      const { selectedCountryId } = state.dashboardEOS.scenarioFilters;

      if (
        !params.supplyContractId ||
        !params.selectedReportTypeIds?.length ||
        !params.dateRange?.[0] ||
        !params.dateRange?.[1]
      ) {
        return { widgetId: params.widgetId, data: [] };
      }

      const [start, end] = params.dateRange.map((date) =>
        dayjs(date).format(DATEFORMAT.yearMonthDay),
      );

      const DELIVERY_POINT_TYPE = 1;
      const hasDeliveryPoint = params.deliveryPointId !== null;
      const hasField = params.fieldId !== null;

      let resolvedPointType: number | null = null;
      if (hasField) {
        resolvedPointType = params.fieldSelection;
      } else if (hasDeliveryPoint) {
        resolvedPointType = DELIVERY_POINT_TYPE;
      }

      let resolvedPointId: number | null = null;
      if (resolvedPointType === DELIVERY_POINT_TYPE) {
        resolvedPointId = params.deliveryPointId;
      } else if (resolvedPointType !== null) {
        resolvedPointId = params.fieldId;
      }

      const response = await eosPost(
        API_URLS.SCENARIO_ANALYSIS_DATA,
        {
          portfolioId: selectedPortfolioId,
          marketId: selectedCountryId,
          supplyContractId: params.supplyContractId,
          reportType: params.selectedReportTypeIds.join(','),
          startDate: start,
          endDate: end,
          volumeTypeId:
            Array.isArray(params.volumeType) && params.volumeType.length > 0
              ? params.volumeType.join(',')
              : params.volumeType?.toString() || null,
          ...(resolvedPointType !== null &&
            resolvedPointId !== null && {
              pointType: resolvedPointType.toString(),
              pointId: resolvedPointId,
            }),
        },
        {
          signal: params.signal,
        },
      );

      return { widgetId: params.widgetId, data: response.data ?? [] };
    } catch (error) {
      if ((error instanceof Error && error.name === 'AbortError') || axios.isCancel(error)) {
        return { widgetId: params.widgetId, data: [] };
      }

      return rejectWithValue({
        widgetId: params.widgetId,
        error: error instanceof Error ? error.message : 'Failed to load data',
      });
    }
  },
);

export const saveWidgetsToAPI = createAsyncThunk(
  'scenarioAnalysis/saveWidgetsToAPI',
  async (
    {
      widgets,
      userId,
      supplyContractId,
    }: { widgets: IWidget[]; userId: number; supplyContractId: number },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as ScenarioAnalysisStateShape;
      const { selectedPortfolioId } = state.dashboardEOS.portfolio;
      const { selectedCountryId } = state.dashboardEOS.scenarioFilters;
      const response = await eosPost(API_URLS.SCENARIO_ANALYSIS_PREFERENCES_SAVE, {
        UpdateScenarioAnalysisPreference: widgets.map((widget, index) => ({
          ...widget,
          zIndex: index + 1,
        })),
        userId,
        supplyContractId,
        portfolioId: selectedPortfolioId,
        marketId: selectedCountryId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(createErrorMessage(error, 'Failed to save widgets'));
    }
  },
);

const scenarioAnalysisSlice = createSlice({
  name: 'scenarioAnalysis',
  initialState,
  reducers: {
    addWidget: (state, { payload: widget }) => {
      const zIndex = state.widgets.maxZIndex + 1;
      const {
        x: _x,
        y: _y,
        width: _width,
        height: _height,
        minWidth: _minWidth,
        minHeight: _minHeight,
        ...widgetData
      } = widget;

      state.widgets.data.push({ ...widgetData, zIndex });
      state.widgets.maxZIndex = zIndex;
      state.widgetLayouts[widget.id] = {
        x: _x,
        y: _y,
        width: _width,
        height: _height,
        minWidth: _minWidth,
        minHeight: _minHeight,
        zIndex,
      };
    },

    removeWidget: (state, { payload: { id } }) => {
      state.widgets.data = state.widgets.data.filter((widget) => widget.id !== id);
      delete state.widgetLayouts[id];
      delete state.widgetData[id];
    },

    updateWidgetLayout: (state, { payload: { id, updates } }) => {
      if (state.widgetLayouts[id]) {
        Object.assign(state.widgetLayouts[id], updates);
      }
    },

    handleDragStop: (state, { payload: { id, x, y } }) => {
      if (state.widgetLayouts[id]) {
        state.widgetLayouts[id].x = x;
        state.widgetLayouts[id].y = y;
      }
    },

    handleResizeStop: (state, { payload: { id, width, height, x, y } }) => {
      if (state.widgetLayouts[id]) {
        state.widgetLayouts[id].width = width;
        state.widgetLayouts[id].height = height;
        if (x !== undefined) {
          state.widgetLayouts[id].x = x;
        }
        if (y !== undefined) {
          state.widgetLayouts[id].y = y;
        }
      }
    },

    bringToFront: (state, { payload: { id } }) => {
      const newZIndex = state.widgets.maxZIndex + 1;
      const widget = state.widgets.data.find((w) => w.id === id);

      if (widget) {
        widget.zIndex = newZIndex;
      }
      if (state.widgetLayouts[id]) {
        state.widgetLayouts[id].zIndex = newZIndex;
      }
      state.widgets.maxZIndex = newZIndex;
    },

    updateWidgetSettings: (state, { payload: { widgetId, updates } }) => {
      state.widgets.data = updateWidgetProperty(state.widgets.data, widgetId, updates);
    },

    setSelectedDateRange: (state, { payload: { startDate, endDate, widgetId } }) => {
      const dateRange: (Dayjs | null)[] = [
        startDate ? startDate.toISOString() : null,
        endDate ? endDate.toISOString() : null,
      ];
      state.widgets.data = updateWidgetProperty(state.widgets.data, widgetId, {
        dateRange,
      });
    },

    clearWidgets: (state) => {
      Object.assign(state, {
        widgets: { ...state.widgets, data: [], maxZIndex: 1 },
        widgetData: {},
        widgetLayouts: {},
        savedWidgets: [],
      });
    },

    syncAllWidgets: (state) => {
      const timestamp = Date.now();
      state.lastRefreshTime = dayjs().format(DATEFORMAT.dayMonthDayWithHoursMinutesSeconds);
      state.widgets.data = state.widgets.data.map((widget) => ({
        ...widget,
        refreshTimestamp: timestamp,
      }));
    },

    refreshWidget: (state, { payload: { widgetId } }) => {
      state.widgets.data = updateWidgetProperty(state.widgets.data, widgetId, {
        refreshTimestamp: Date.now(),
      });
    },

    clearWidgetData: (state, { payload: { widgetId } }) => {
      if (state.widgetData[widgetId]) {
        state.widgetData[widgetId] = createDefaultWidgetDataState();
      }
    },
  },
  extraReducers: (builder) => {
    // Widget management async thunks
    builder
      .addCase(fetchWidgetsBasedOnFilters.pending, (state) => {
        state.widgets.loading = true;
      })
      .addCase(fetchWidgetsBasedOnFilters.fulfilled, (state, { payload }) => {
        const widgets: IWidget[] = Array.isArray(payload) ? payload : [];

        state.widgets.data = widgets.map((widget) => {
          const {
            x: _x,
            y: _y,
            width: _width,
            height: _height,
            minWidth: _minWidth,
            minHeight: _minHeight,
            zIndex: _zIndex,
            ...widgetData
          } = widget;
          return widgetData as IWidget;
        });

        state.widgetLayouts = widgets.reduce(
          (acc, widget) => {
            const {
              id,
              x = 50,
              y = 50,
              width: widgetWidth = 600,
              height: widgetHeight = 400,
              minWidth: widgetMinWidth = 300,
              minHeight: widgetMinHeight = 200,
              zIndex: widgetZIndex = 1,
            } = widget;
            acc[id] = {
              x,
              y,
              width: widgetWidth,
              height: widgetHeight,
              minWidth: widgetMinWidth,
              minHeight: widgetMinHeight,
              zIndex: widgetZIndex,
            };
            state.widgets.maxZIndex = Math.max(state.widgets.maxZIndex, widgetZIndex);
            return acc;
          },
          {} as typeof state.widgetLayouts,
        );

        // Snapshot the freshly loaded widgets as the "saved" baseline. Written
        // here, alongside the data, so the two can never disagree by timing.
        state.savedWidgets = normalizeWidgets(
          mergeWidgetsWithLayouts(state.widgets.data, state.widgetLayouts),
        );

        state.widgets.loading = false;
      })
      .addCase(fetchWidgetsBasedOnFilters.rejected, (state, { error }) => {
        state.widgets.error = error.message ?? null;
        state.widgets.loading = false;
      })
      .addCase(saveWidgetsToAPI.pending, (state) => {
        state.widgets.saving = true;
      })
      .addCase(saveWidgetsToAPI.fulfilled, (state) => {
        state.widgets.saving = false;
        state.widgets.error = null;
        // What's on screen is now persisted, so it becomes the new baseline.
        state.savedWidgets = normalizeWidgets(
          mergeWidgetsWithLayouts(state.widgets.data, state.widgetLayouts),
        );
      })
      .addCase(saveWidgetsToAPI.rejected, (state, { error }) => {
        state.widgets.saving = false;
        state.widgets.error = error.message ?? 'Failed to save widgets';
      })
      .addCase(
        fetchWidgetData.pending,
        (
          state,
          {
            meta: {
              arg: { widgetId },
            },
          },
        ) => {
          state.widgetData[widgetId] = state.widgetData[widgetId] ?? createDefaultWidgetDataState();
          state.widgetData[widgetId].loading = true;
          state.widgetData[widgetId].error = null;
        },
      )
      .addCase(fetchWidgetData.fulfilled, (state, { payload: { widgetId, data } }) => {
        const { chartData, seriesKeys, seriesUomObj } = processWidgetData(data);
        state.widgetData[widgetId] = {
          loading: false,
          error: null,
          rawData: data,
          chartData,
          seriesKeys,
          seriesUoms: seriesUomObj,
        };
      })
      .addCase(fetchWidgetData.rejected, (state, { payload }) => {
        const { widgetId, error } = payload as {
          widgetId: string;
          error: string;
        };
        if (widgetId) {
          state.widgetData[widgetId] = state.widgetData[widgetId] ?? createDefaultWidgetDataState();
          state.widgetData[widgetId].loading = false;
          state.widgetData[widgetId].error = error;
        }
      });
  },
});

export const scenarioAnalysisActions = scenarioAnalysisSlice.actions;
export default scenarioAnalysisSlice.reducer;

export {
  getAllWidgetsWithLayouts,
  getLastRefreshTimeState,
  getWidgetById,
  getWidgetDataState,
  getWidgetLayout,
  getWidgetsState,
  getWidgetWithLayout,
  selectHasUnsavedChanges,
} from '../selectors/scenarioAnalysisSelectors';
